<template>
  <div class="packet-view">
    <header class="packet-header">
      <div>
        <button class="btn btn-link" @click="$router.back()">
          <i class="fas fa-arrow-left"></i> Back
        </button>
        <h1 v-if="run">
          Quarterly Review — {{ run.household_name }}
        </h1>
      </div>
      <div class="header-actions" v-if="run">
        <a
          v-if="run.packet_pdf_url"
          :href="pdfDownloadUrl"
          target="_blank"
          class="btn btn-primary"
        >
          <i class="fas fa-download"></i> Download PDF
        </a>
      </div>
    </header>

    <div v-if="loading" class="loading-state">
      <p>Loading review packet…</p>
    </div>

    <div v-else-if="!run" class="empty-state">
      <p>Run not found.</p>
    </div>

    <div v-else class="packet-body">
      <section class="summary-card">
        <div class="summary-row">
          <div>
            <div class="label">Total AUM</div>
            <div class="value">${{ formatCurrency(run.aum_snapshot) }}</div>
          </div>
          <div>
            <div class="label">Cash</div>
            <div class="value">${{ formatCurrency(run.cash_snapshot) }}</div>
          </div>
          <div>
            <div class="label">IPS Score</div>
            <div class="value" :class="scoreClass(run.overall_score)">
              {{ run.overall_score }}/100
            </div>
          </div>
          <div>
            <div class="label">Findings</div>
            <div class="value">{{ run.finding_count }}</div>
          </div>
          <div>
            <div class="label">Period</div>
            <div class="value small">
              {{ formatDate(run.period_start) }} – {{ formatDate(run.period_end) }}
            </div>
          </div>
          <div>
            <div class="label">Status</div>
            <div class="value small">
              <span class="badge" :class="`badge-${run.status.toLowerCase()}`">
                {{ run.status }}
              </span>
            </div>
          </div>
        </div>
      </section>

      <section v-if="run.findings && run.findings.length" class="findings-section">
        <h2><i class="fas fa-clipboard-check"></i> Findings</h2>
        <div
          v-for="(f, idx) in sortedFindings"
          :key="idx"
          class="finding-card"
          :class="`severity-${f.severity}`"
        >
          <div class="finding-head">
            <span class="severity-badge">{{ f.severity }}</span>
            <strong>{{ f.title }}</strong>
            <span class="code">{{ f.code }}</span>
          </div>
          <p v-if="f.narrative" class="narrative">{{ f.narrative }}</p>
          <div v-if="f.action_items && f.action_items.length" class="actions">
            <strong>Action items:</strong>
            <ul>
              <li v-for="(a, i) in f.action_items" :key="i">
                {{ humanizeAction(a) }}
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section v-if="run.packet_html" class="packet-preview">
        <h2><i class="fas fa-file-alt"></i> Packet Preview</h2>
        <iframe
          class="packet-iframe"
          :srcdoc="run.packet_html"
          sandbox="allow-same-origin"
        ></iframe>
      </section>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'

export default {
  name: 'QuarterlyReviewPacket',

  data() {
    return {
      run: null,
      loading: false,
    }
  },

  computed: {
    sortedFindings() {
      const order = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 }
      return [...(this.run.findings || [])].sort(
        (a, b) => (order[a.severity] ?? 4) - (order[b.severity] ?? 4),
      )
    },
    pdfDownloadUrl() {
      return `/api/wealth/review-runs/${this.$route.params.id}/packet.pdf/`
    },
  },

  mounted() {
    this.load()
  },

  methods: {
    async load() {
      this.loading = true
      try {
        const { data } = await api.get(`/wealth/review-runs/${this.$route.params.id}/`)
        this.run = data
      } catch (err) {
        console.error('Failed to load review:', err)
      } finally {
        this.loading = false
      }
    },
    formatCurrency(val) {
      return Number(val || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })
    },
    formatDate(d) {
      if (!d) return '—'
      return new Date(d).toLocaleDateString()
    },
    scoreClass(score) {
      if (score == null) return ''
      if (score >= 85) return 'score-good'
      if (score >= 65) return 'score-ok'
      return 'score-bad'
    },
    humanizeAction(a) {
      return String(a).replaceAll('_', ' ').replace(/\b\w/g, c => c.toUpperCase())
    },
  },
}
</script>

<style scoped>
.packet-view {
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
}
.packet-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 20px;
}
.packet-header h1 {
  margin: 8px 0 0 0;
  color: #0f2b4a;
}
.summary-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
}
.summary-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 16px;
}
.label {
  color: #64748b;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.value {
  font-size: 20px;
  font-weight: 700;
  color: #0f2b4a;
}
.value.small { font-size: 13px; }
.score-good { color: #15803d; }
.score-ok { color: #b45309; }
.score-bad { color: #b91c1c; }
.findings-section { margin-bottom: 24px; }
.findings-section h2 { color: #0f2b4a; margin-bottom: 12px; }
.finding-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-left-width: 4px;
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 10px;
}
.severity-CRITICAL { border-left-color: #dc2626; }
.severity-HIGH { border-left-color: #f97316; }
.severity-MEDIUM { border-left-color: #eab308; }
.severity-LOW { border-left-color: #16a34a; }
.finding-head {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.finding-head strong { color: #0f2b4a; }
.severity-badge {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
  background: #f1f5f9;
  color: #334155;
  letter-spacing: 0.04em;
}
.severity-CRITICAL .severity-badge { background: #fee2e2; color: #991b1b; }
.severity-HIGH     .severity-badge { background: #ffedd5; color: #9a3412; }
.severity-MEDIUM   .severity-badge { background: #fef9c3; color: #854d0e; }
.severity-LOW      .severity-badge { background: #dcfce7; color: #166534; }
.code {
  margin-left: auto;
  color: #64748b;
  font-family: monospace;
  font-size: 11px;
}
.narrative {
  color: #475569;
  margin-top: 6px;
  font-style: italic;
}
.actions ul {
  margin: 4px 0 0 16px;
  padding: 0;
  color: #334155;
  font-size: 13px;
}
.badge {
  display: inline-block; padding: 2px 8px; border-radius: 10px;
  font-size: 11px; font-weight: 600;
}
.badge-completed { background: #dcfce7; color: #166534; }
.badge-pending, .badge-in_progress { background: #fef3c7; color: #854d0e; }
.badge-failed { background: #fee2e2; color: #991b1b; }
.packet-preview h2 { color: #0f2b4a; margin-bottom: 12px; }
.packet-iframe {
  width: 100%;
  min-height: 900px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
}
.btn {
  padding: 8px 14px;
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.btn-primary { background: #0f2b4a; color: #fff; }
.btn-link { background: transparent; color: #0f2b4a; }
.loading-state, .empty-state { text-align: center; padding: 40px; color: #64748b; }
</style>
